import {
  Avatar,
  Button,
  Dropdown,
  Image,
  TableColumnsType,
  Tag,
  Tooltip,
} from "antd";
import { PhoneInput } from "react-international-phone";
import Header from "../../atoms/Header";
import TableUi from "../../atoms/Table";
import DashboardTemplate from "../../templates/Dashboard";
import {
  getInitials,
  PAGE_LIMIT,
  useStableRandomColors,
} from "../../config/utils";
import Filters from "./Components/Filters";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../config/hooks";
import { UsersTableType } from "../Team/ducks/types";
import { getUsersListing, removeUsersListingData } from "../Team/ducks/actions";
import { useNavigate } from "react-router-dom";
import { CaretDownOutlined, MoreOutlined } from "@ant-design/icons";
import { CrossIcon, DeleteIcon, TickIcon } from "../../atoms/CustomIcons";
import { AxiosError } from "axios";
import { ErrorMessage } from "../../atoms/Toasts/ErrorMessage";
import { PopUp } from "../../atoms/PopUp";
import ConfirmDelete from "../../atoms/ConfirmDelete";
import ChangePasswordPopUp from "../MyProfile/Components/ChangePasswordPopUp";
import EmptyCard from "../../atoms/EmptyCard";
import { changeUserStatus, deleteUser } from "../Team/ducks/services";
import { SuccessMessage } from "../../atoms/Toasts/SuccessMessage";
import { formatRelativeTime } from "../../atoms/TimeFormatDisplay";

export default function CustomerUsers() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userListing = useAppSelector((state) => state?.team?.usersData);
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(PAGE_LIMIT);
  const [tableRecord, setTableRecord] = useState<UsersTableType | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeactivatedModalOpen, setIsDeactivatedModalOpen] = useState(false);
  const [isAllDeleteModalOpen, setIsAllDeleteModalOpen] = useState(false);
  const [isChangePswModalOpen, setIsChangePswModalOpen] = useState(false);
  const [deactivated, isDeactivated] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const avatarColor = useStableRandomColors(PAGE_LIMIT);

  const [filters, setFilters] = useState<{
    search_text: string | undefined;
    deactivated: boolean | undefined;
    company: string[] | undefined;
    position: string | undefined;
    language: string[] | undefined;
    status: string | undefined;
  }>({
    search_text: undefined,
    deactivated: false,
    company: undefined,
    position: undefined,
    language: undefined,
    status: undefined,
  });

  const confirmDeactivatedPopUp = {
    visibility: isDeactivatedModalOpen,
    content: (
      <ConfirmDelete
        title=""
        isArchivePopup={true}
        onClose={() => setIsDeactivatedModalOpen(false)}
        heading="Deactivate user"
        paragraph={
          <>
            Are you sure you want to deactivate{" "}
            <b>
              {tableRecord?.first_name} {tableRecord?.last_name}
            </b>
            ?<br /> This user won't be able to access their account anymore. The
            data associated with this user (e.g., work history) will be
            preserved.
          </>
        }
        btnText="Deactivate user"
        btnApi={() => functionChangeStatus("deactivated")}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeactivatedModalOpen(false),
  };

  const fetchUserListing = () => {
    const payload = {
      type: ["client"],
      status: ["active", "invited"],
      offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
      limit: limit,
    };
    dispatch(getUsersListing(payload));
  };

  const changeStatus = (val: string) => {
    if (val === "deactivated") {
      setIsDeactivatedModalOpen(true);
      return;
    }
    setLoading(true);
    functionChangeStatus(val);
  };

  const functionChangeStatus = (val: string) => {
    const payload = {
      status: val,
    };
    changeUserStatus(tableRecord?.id || "", payload)
      .then(() => {
        setLoading(false);
        setIsDeactivatedModalOpen(false);
        isDeactivated(false);
        fetchUserListing();
        SuccessMessage(
          val === "deactivated" ? "User deactivated" : "User Activated"
        );
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const items = [
    {
      key: "1",
      deactivated: true,
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={
            <Image
              src="/images/table/eye-icon.svg"
              preview={false}
              style={{ opacity: 0.7 }}
            />
          }
          onClick={(e) => {
            e.stopPropagation();
          }}
          disabled
          style={{ opacity: 0.7 }}
        >
          View with user’s eyes
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/edit-user/${tableRecord?.id}`);
          }}
        >
          Edit
        </Button>
      ),
    },
    {
      key: "3",
      disabled: tableRecord?.status === "deactivated",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={
            <Image
              src="/images/table/password-icon.svg"
              preview={false}
              style={{
                opacity: tableRecord?.status === "deactivated" ? 0.7 : 1,
              }}
            />
          }
          onClick={(e) => {
            e.stopPropagation();
            setIsChangePswModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
          disabled={tableRecord?.status === "deactivated"}
          style={{ opacity: tableRecord?.status === "deactivated" ? 0.7 : 1 }}
        >
          Change password
        </Button>
      ),
    },
    {
      key: "4",
      label: (
        <Button
          type="text"
          className="delete-hover"
          icon={<DeleteIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const itemsInvited = [
    {
      key: "1",
      deactivated: true,
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={
            <Image
              src="/images/table/eye-icon.svg"
              preview={false}
              style={{ opacity: 0.7 }}
            />
          }
          onClick={(e) => {
            e.stopPropagation();
          }}
          disabled
          style={{ opacity: 0.7 }}
        >
          View with user’s eyes
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/edit-user/${tableRecord?.id}`);
          }}
        >
          Edit
        </Button>
      ),
    },

    {
      key: "3",
      label: (
        <Button
          type="text"
          className="delete-hover"
          icon={<DeleteIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const statusItems = [
    {
      key: "header",
      label: (
        <div
          style={{
            padding: "6px 12px",
            color: "#474C4D",
            fontWeight: 500,
            fontSize: "12px",
            cursor: "default",
          }}
        >
          Change status to:
        </div>
      ),
      disabled: true,
    },
    {
      key: "active",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("active");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#00A07D", borderRadius: "2px" }}
          />{" "}
          Active{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "deactivated",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("deactivated");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#6C7478", borderRadius: "2px" }}
          />{" "}
          Deactivated{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
  ];

  const statusItemsInvited = [
    {
      key: "invited",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("invited");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#2098EE", borderRadius: "2px" }}
          />{" "}
          Invited{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "active",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("active");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#00A07D", borderRadius: "2px" }}
          />{" "}
          Active{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
  ];

  const columns: TableColumnsType<UsersTableType> = [
    {
      title: "User name",
      dataIndex: "first_name",
      width: 216,
      fixed: "left",
      sorter: (a, b) => a?.first_name?.localeCompare(b?.first_name),
      render: (text, record, index) => {
        return (
          <div className="text_with_avatar">
            {record?.image_url ? (
              <div className="participant_avatar">
                <Avatar src={record?.image_url} />
              </div>
            ) : (
              <div className="participant_avatar">
                <Avatar
                  style={{ backgroundColor: avatarColor?.[index] ?? "#ccc" }}
                >
                  {getInitials(record?.first_name, record?.last_name)}
                </Avatar>
              </div>
            )}
            <div className="text_wrap">
              <Tooltip title={`${text} ${record?.last_name}`}>
                {`${text} ${record?.last_name}`}
              </Tooltip>
              {record?.status === "invited" && (
                <span style={{ color: "#98A3A9" }}> (invited)</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Company",
      dataIndex: "company",
      width: 200,
      render: (text) => (
        <Tooltip title={text?.name ?? "-"}>
          <span className="table-col-ellipsis" style={{ width: "190px" }}>
            {text?.name ?? "-"}
          </span>
        </Tooltip>
      ),

      sorter: (a, b) => a?.company?.name?.localeCompare(b?.company?.name),
    },
    {
      title: "Position",
      dataIndex: "position_id",
      width: 200,
      render: (text) => {
        let positionName = "-";
        let tagClass = "default_tag";

        if (text === "1") {
          positionName = "Manager";
          tagClass = "blue_tag";
        } else if (text === "2") {
          positionName = "Crew leader";
          tagClass = "green_tag";
        } else if (text === "3") {
          positionName = "Technician";
          tagClass = "orange_tag";
        } else if (text === "4") {
          positionName = "Account manager";
          tagClass = "yellow_tag";
        } else if (text === "5") {
          positionName = "Production manager";
          tagClass = "red_tag";
        } else if (text === "6") {
          positionName = "CEO";
          tagClass = "violet_tag";
        }

        return positionName !== "-" ? (
          <Tag className={tagClass}>{positionName}</Tag>
        ) : (
          "-"
        );
      },
      sorter: (a, b) => a?.position_id?.localeCompare(b?.position_id),
    },
    {
      title: "First language",
      dataIndex: "first_language",
      width: 132,
      render: (text) =>
        text ? <div style={{ textTransform: "capitalize" }}>{text}</div> : "-",
    },
    {
      title: "Account email",
      dataIndex: "email",
      width: 250,
      render: (text) => text ?? "-",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      width: 150,
      render: (text) => (text ? <PhoneInput disabled value={text} /> : "-"),
    },
    {
      title: "User status",
      dataIndex: "status",
      width: 116,
      render: (text, record) => (
        <Dropdown
          menu={{
            items: text === "invited" ? statusItemsInvited : statusItems,
            selectable: true,
            selectedKeys: [text],
          }}
          trigger={["click"]}
          destroyPopupOnHide
          overlayClassName="status_dropdown_overlay no-hover-color"
        >
          <Button
            onClick={(e) => {
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className={`status_dropdown ${text}_status`}
            ref={dropdownRef}
          >
            <span style={{ textTransform: "capitalize" }}>{text}</span>{" "}
            <div className="icon_arrow">
              <CaretDownOutlined />
            </div>
          </Button>
        </Dropdown>
      ),
    },
    {
      title: "Last activity",
      dataIndex: "last_user_activity",
      width: 125,
      render: (text) => (text ? formatRelativeTime(text) : "-"),
    },
    {
      title: <Image src="/images/table/menu-burger.svg" preview={false} />,
      dataIndex: "action",
      width: 52,
      fixed: "right",
      render: (_text, record) => (
        <Dropdown
          menu={{
            items: record?.status === "invited" ? itemsInvited : items,
          }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              console.log("text", record);
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "6px",
              height: "auto",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
            ref={dropdownRef}
          />
        </Dropdown>
      ),
    },
  ];

  useEffect(() => {
    const payload = {
      type: ["client"],
      status: ["active", "invited"],
      offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
      limit: limit,
    };
    dispatch(getUsersListing(payload));
  }, [dispatch, pagination, limit]);

  useEffect(() => {
    const payload = {
      type: ["client"],
      status: filters?.status ? [filters?.status] : ["active", "invited"],
      offset: 0,
      limit: PAGE_LIMIT,
      position_id: filters?.position ? [filters?.position] : undefined,
      company_id:
        filters?.company && filters?.company?.length > 0
          ? filters?.company
          : undefined,
      search_text: filters?.search_text ?? undefined,
      first_language:
        filters?.language && filters?.language?.length > 0
          ? filters?.language
          : undefined,
    };
    dispatch(getUsersListing(payload));
  }, [dispatch, filters]);

  const deleteFunction = () => {
    setLoading(true);
    deleteUser(
      selectedRowKeys?.length > 0 ? selectedRowKeys : [tableRecord?.id || ""]
    )
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setIsAllDeleteModalOpen(false);
        setSelectedRowKeys([]);
        fetchUserListing();
        SuccessMessage("User deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const confirmDeletePopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete{" "}
            <b>
              {tableRecord?.first_name} {tableRecord?.last_name}
            </b>
            ? This action can't be undone.
          </>
        }
        btnText="Delete user"
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const changePasswordPopup = {
    visibility: isChangePswModalOpen,
    content: (
      <ChangePasswordPopUp
        onClose={() => setIsChangePswModalOpen(false)}
        id={tableRecord?.id || ""}
      />
    ),
    width: 640,
    onCancel: () => setIsChangePswModalOpen(false),
  };

  const deleteAllUsersPopUp = {
    visibility: isAllDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsAllDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete <b>all users</b>
            ?<br /> This action can't be undone.
          </>
        }
        btnText="Delete user"
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsAllDeleteModalOpen(false),
  };

  useEffect(() => {
    dispatch(removeUsersListingData());
  }, [dispatch]);

  // Ensure current page is within available range after data changes (e.g., archiving/deleting items)
  useEffect(() => {
    const count = userListing?.count ?? 0;
    const totalPages = Math.max(1, Math.ceil(count / limit));
    if (pagination > totalPages) {
      setPagination(totalPages);
    }
  }, [userListing?.count, limit, pagination]);

  return (
    <DashboardTemplate loading={userListing?.loader || loading}>
      <Header
        title="Customer users"
        description="The list of all users of all companies in LandScaling."
      />
      <TableUi<UsersTableType>
        columns={columns}
        data={{
          count: userListing?.count,
          data: userListing?.data || [],
        }}
        filters={
          <Filters
            setFilters={setFilters}
            filters={filters}
            deactivated={deactivated}
            isDeactivated={isDeactivated}
          />
        }
        pageSize={limit}
        setPageSize={setLimit}
        setCurrentPage={setPagination}
        currentPage={pagination}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        createButtonText="Create user"
        createButtonLink="/create-user"
      />
      {userListing?.count === 0 && (
        <div className="empty_wrap">
          <EmptyCard
            imageHeight={220}
            title={
              deactivated
                ? "There are no deactivated users"
                : filters?.search_text
                ? "No results match the filter or search criteria"
                : `There are no ${filters?.status ?? "active"} clients`
            }
            description={
              deactivated
                ? "Once the user is deactivated, they will appear in this list."
                : filters?.search_text
                ? "Try adjusting the filters and search."
                : ""
            }
            image={
              deactivated
                ? "/images/empty-icons/no-active.svg"
                : filters?.search_text
                ? "/images/empty-icons/no-active.svg"
                : "/images/empty-icons/no-active.svg"
            }
          />
        </div>
      )}
      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar" style={{ minWidth: "235px" }}>
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${selectedRowKeys.length === 1 ? "user" : "users"}`} selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon />}
              onClick={() => {
                setIsAllDeleteModalOpen(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}
      <PopUp {...confirmDeletePopUp} />
      <PopUp {...deleteAllUsersPopUp} />
      <PopUp {...confirmDeactivatedPopUp} />
      <PopUp {...changePasswordPopup} />
    </DashboardTemplate>
  );
}
